/**
 * MKS-339 — the client-request inbox over TASKS, with its SLA columns and
 * in-place triage. Moved out of `HelpdeskPage` unchanged by MKS-410.
 *
 * WHO SEES THIS NOW. MKS-410 gives an ADMIN the unified request queue (both
 * sources, over the request record). This view stays for everyone else: it is
 * scoped server-side to the caller's own intake-enabled projects, so a project
 * member who handles client requests keeps exactly the inbox, the SLA clocks
 * and the triage panel MKS-339 built for them — none of which the request
 * record carries, because an internal request has no SLA.
 *
 * FILTERS LIVE IN THE URL. `useSearchParams` is the single source of truth —
 * there is no `useState` mirror of the filter set — so a filtered inbox is a
 * shareable link, the browser's back button walks the filter history, and a
 * reload lands on the same view. Reading the filters back out of the URL on
 * every render is what makes that true; a mirrored state would drift the
 * moment someone edited the address bar.
 */
import { useSearchParams } from 'react-router-dom';
import { Fragment, useState } from 'react';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { Btn } from '../ui/Btn';
import { HelpdeskFilters } from './HelpdeskFilters';
import { TriagePanel } from './TriagePanel';
import { SLA_TONES, STATUS_TONES, formatMilestone, formatRelative } from './slaFormat';
import { useHelpdeskRequests, useHelpdeskFilterOptions } from '../../hooks/useHelpdesk';
import type { HelpdeskFilters as Filters } from '../../services/api/helpdesk';

/** Every filter key the URL may carry, so none is read or written ad hoc. */
const FILTER_KEYS = [
  'view',
  'status',
  'slaState',
  'projectId',
  'customerId',
  'categoryId',
  'owner',
] as const;

function filtersFromParams(params: URLSearchParams): Filters {
  const filters: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') filters[key] = value;
  }
  return filters as Filters;
}

export function ClientRequestInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null);

  const filters = filtersFromParams(searchParams);

  const { data: requests, isLoading, isError } = useHelpdeskRequests(filters);
  const { data: options } = useHelpdeskFilterOptions();

  function handleFilterChange(key: keyof Filters, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete(key);
    else next.set(key, value);
    // `replace: false` so each filter change is its own history entry and the
    // back button walks them.
    setSearchParams(next);
    setExpandedTaskId(null);
  }

  function handleReset() {
    setSearchParams(new URLSearchParams());
    setExpandedTaskId(null);
  }

  const rows = requests ?? [];

  return (
    <>
      <HelpdeskFilters
        filters={filters}
        options={options}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      <Card className="overflow-hidden">
        {isLoading && <p className="text-[12.5px] text-ms-text-muted">Loading requests…</p>}

        {isError && (
          <p className="text-[12.5px] text-rose-600">
            Could not load the inbox. Refresh to try again.
          </p>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <p className="text-[12.5px] text-ms-text-muted" data-testid="helpdesk-empty">
            No requests match this view. Projects appear here once external intake is
            enabled on them and a client has submitted a request.
          </p>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider text-ms-text-muted">
                  <th className="py-2 pr-3 font-semibold">Code</th>
                  <th className="py-2 pr-3 font-semibold">Request</th>
                  <th className="py-2 pr-3 font-semibold">Customer</th>
                  <th className="py-2 pr-3 font-semibold">Project</th>
                  <th className="py-2 pr-3 font-semibold">Requester</th>
                  <th className="py-2 pr-3 font-semibold">Category</th>
                  <th className="py-2 pr-3 font-semibold">Tier</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                  <th className="py-2 pr-3 font-semibold">Response</th>
                  <th className="py-2 pr-3 font-semibold">Resolution</th>
                  <th className="py-2 pr-3 font-semibold">Owner</th>
                  <th className="py-2 pr-3 font-semibold">Last activity</th>
                  <th className="py-2 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {rows.map((request) => {
                  const slaTone = SLA_TONES[request.slaState];
                  const statusTone = STATUS_TONES[request.status];
                  const expanded = expandedTaskId === request.taskId;
                  return (
                    // A row and its triage panel are two <tr>s that must stay
                    // siblings inside <tbody>, so the key goes on the Fragment.
                    <Fragment key={request.taskId}>
                      <tr
                        className="border-t border-ms-border align-middle"
                        data-testid={`helpdesk-row-${request.taskId}`}
                      >
                        <td className="py-2 pr-3 font-mono text-[11.5px] text-ms-text-secondary">
                          {request.code}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-strong">{request.name}</td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.customerName ?? '—'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.projectName}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.requesterName ?? '—'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.categoryName ?? '—'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.tierName ?? '—'}
                        </td>
                        <td className="py-2 pr-3">
                          <Pill bg={statusTone.bg} fg={statusTone.fg}>
                            {statusTone.label}
                          </Pill>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="text-ms-text-secondary">
                            {formatMilestone(request.response)}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <span className="text-ms-text-secondary">
                              {formatMilestone(request.resolution)}
                            </span>
                            {request.slaState !== 'none' && (
                              <Pill bg={slaTone.bg} fg={slaTone.fg}>
                                {slaTone.label}
                              </Pill>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.ownerName ?? 'Unassigned'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {formatRelative(request.lastActivityAt)}
                        </td>
                        <td className="py-2">
                          <Btn
                            variant="secondary"
                            size="sm"
                            aria-expanded={expanded}
                            onClick={() =>
                              setExpandedTaskId(expanded ? null : request.taskId)
                            }
                          >
                            {expanded ? 'Close' : 'Triage'}
                          </Btn>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={13} className="p-0">
                            <TriagePanel request={request} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
